import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { FeedView } from './views/FeedView';
import { InsightView } from './views/InsightView';
import { SearchView } from './views/SearchView';
import { SourcesView } from './views/SourcesView';
import { ReviewView } from './views/ReviewView';
import { TelegramView } from './views/TelegramView';
import ChatView from './views/ChatView.js';
import GraphView from './views/GraphView.js';
import AddView from './views/AddView.js';
import DigestsView from './views/DigestsView.js';
import AdvisorView from './views/AdvisorView.js';
import GitView from './views/GitView.js';
import SettingsView from './views/SettingsView.js';
import { CommandPalette } from './components/CommandPalette';
import { useAuth } from './contexts/AuthContext';
import { LoginView } from './views/LoginView';

const NAV_ITEMS = [
  { to: '/', label: 'Feed', icon: '◉' },
  { to: '/search', label: 'Search', icon: '⌕' },
  { to: '/chat', label: 'Chat', icon: '💬' },
  { to: '/graph', label: 'Graph', icon: '🕸' },
  { to: '/git', label: 'Git', icon: '⎇' },
  { to: '/digests', label: 'Digests', icon: '📅' },
  { to: '/advisor', label: 'Advisor ✦', icon: '✦' },
  { to: '/add', label: 'Add', icon: '➕' },
  { to: '/telegram', label: 'Telegram', icon: '✈' },
  { to: '/sources', label: 'Sources', icon: '⟳' },
  { to: '/review', label: 'Review', icon: '⋯' },
  { to: '/settings', label: 'Settings', icon: '⚙' },
];

export function App() {
  const { isAuthenticated, isLoading, logout } = useAuth();
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setCommandPaletteOpen((prev) => !prev);
    }
    if (e.key === 'Escape') {
      setCommandPaletteOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [sidebarOpen]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-obsidian-bg">
        <p className="text-sm text-obsidian-text-muted">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginView />;
  }

  return (
    <div className="flex h-screen h-[100dvh]">
      {/* Sidebar */}
      <nav className={`fixed inset-y-0 left-0 z-40 w-64 bg-obsidian-bg-secondary border-r border-obsidian-border flex flex-col transform transition-transform duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0 md:w-56 md:flex-shrink-0`}>
        <div className="p-4 border-b border-obsidian-border">
          <h1 className="text-sm font-semibold text-obsidian-text tracking-wide">
            Claude Code Insights
          </h1>
        </div>

        {/* Search trigger */}
        <button
          onClick={() => { setCommandPaletteOpen(true); setSidebarOpen(false); }}
          className="mx-3 mt-3 flex items-center gap-2 px-3 py-1.5 text-sm text-obsidian-text-muted bg-obsidian-bg-tertiary border border-obsidian-border rounded-lg hover:border-obsidian-border-light transition-colors"
        >
          <span>⌕</span>
          <span className="flex-1 text-left">Search...</span>
          <kbd className="text-xs text-obsidian-text-faint hidden md:inline">⌘K</kbd>
        </button>

        {/* Navigation */}
        <div className="flex-1 mt-4 space-y-0.5 px-2">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-obsidian-surface text-obsidian-text'
                    : 'text-obsidian-text-muted hover:bg-obsidian-bg-tertiary hover:text-obsidian-text'
                }`
              }
            >
              <span className="text-base">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-obsidian-border">
          <button
            onClick={logout}
            className="text-xs text-obsidian-text-faint hover:text-obsidian-text transition-colors"
          >
            Sign out
          </button>
        </div>
      </nav>

      {/* Sidebar backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <div className="flex items-center gap-3 h-12 px-4 border-b border-obsidian-border bg-obsidian-bg-secondary md:hidden flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-obsidian-text"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <line x1="3" y1="5" x2="17" y2="5" />
              <line x1="3" y1="10" x2="17" y2="10" />
              <line x1="3" y1="15" x2="17" y2="15" />
            </svg>
          </button>
          <span className="text-sm font-semibold text-obsidian-text flex-1">Claude Code Insights</span>
        </div>

        <div className="flex-1 overflow-auto">
          <Routes>
            <Route path="/" element={<FeedView />} />
            <Route path="/insight/:id" element={<InsightView />} />
            <Route path="/search" element={<SearchView />} />
            <Route path="/chat" element={<ChatView />} />
            <Route path="/graph" element={<GraphView />} />
            <Route path="/digests" element={<DigestsView />} />
            <Route path="/advisor" element={<AdvisorView />} />
            <Route path="/git" element={<GitView />} />
            <Route path="/settings" element={<SettingsView />} />
            <Route path="/add" element={<AddView />} />
            <Route path="/telegram" element={<TelegramView />} />
            <Route path="/sources" element={<SourcesView />} />
            <Route path="/review" element={<ReviewView />} />
          </Routes>
        </div>
      </main>

      {/* Command palette */}
      {commandPaletteOpen && (
        <CommandPalette onClose={() => setCommandPaletteOpen(false)} />
      )}
    </div>
  );
}
